// client/src/components/shell/PfTopBar.js
//
// The slim top bar at the top of every team-detail screen.
// Left:   hamburger menu icon
// Center: team crest + name + tier (stacked)
// Right:  role pill (PARENT / COACH / PLAYER)
//
// The hamburger opens the app-level drawer (PfAppDrawer) — same drawer
// as AppChromeLayout, but passed teamContext so it can show the
// "Switch team" affordance. This gives mobile users a consistent
// escape hatch out of a team's chrome without needing a separate
// back button pattern.
//
// Pure-props for now — data (teamName, tier, role, logoUrl, onMenuClick)
// is passed in from TeamDetailLayout, which owns drawer state.
//
// Height stays in lockstep with PfShell's pt-14 (56px). If we ever
// change one, change both.
//
// Usage:
//   <PfTopBar
//     teamName="Coastline Polar Bears"
//     tier="U12 AA · 25–26"
//     role="PARENT"
//     logoUrl={null}
//     onMenuClick={() => setMenuOpen(true)}
//   />

// Role → pill tone. The pill styling matches Sticker's tone system
// but with tighter dimensions (this is chrome, not a callout).
const ROLE_TONE = {
  PARENT: 'bg-accentSoft text-accentDeep border-accent/40',
  COACH:  'bg-marker/15  text-marker    border-marker/40',
  PLAYER: 'bg-green/15   text-green     border-green/40',
};

export function PfTopBar({
  teamName,
  tier,
  role,
  logoUrl,
  onMenuClick,
  className = '',
}) {
  const roleTone = ROLE_TONE[role] ?? ROLE_TONE.PARENT;

  return (
    // Fixed-position row inside PfShell's relative frame. Three regions
    // now — hamburger absolute-left, identity absolute-centered, role
    // pill absolute-right. Using absolute positioning for the center
    // cluster so it stays truly centered regardless of the left/right
    // element widths (matches PfAppTopBar's pattern).
    <header
      className={[
        'absolute top-0 left-0 right-0 h-14',
        'bg-paper border-b border-rule',
        'flex items-center',
        'px-screenX',
        className,
      ].join(' ')}
    >
      {/* Hamburger — left. Same wavy-line SVG as PfAppTopBar for
          consistency; user gets the same "menu is top-left" muscle
          memory whether they're at app-level or inside a team. */}
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="Open menu"
        className="
          h-10 w-10 -ml-1
          flex items-center justify-center
          rounded-full
          active:bg-ink/5
          flex-shrink-0
        "
      >
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path d="M3 6.5 Q11 5.8 19 6.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M3 11 Q11 10.6 19 11.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M3 15.5 Q11 15 19 15.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {/* Center cluster: crest + name/tier. Absolute-positioned + centered
          so it stays visually center regardless of left/right widths.
          pointer-events-none so it doesn't intercept taps meant for the
          hamburger or role pill (the crest/name aren't tappable anyway).
          max-w constrained so long team names truncate rather than
          overlapping the hamburger or role pill. */}
      <div className="
        absolute left-1/2 -translate-x-1/2
        flex items-center gap-3 min-w-0
        max-w-[60%]
        pointer-events-none
      ">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="w-7 h-7 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div
            aria-hidden="true"
            className="w-7 h-7 rounded-full bg-accentSoft border border-accent/40 flex-shrink-0"
          />
        )}

        <div className="min-w-0 leading-tight text-center">
          <p className="font-sans font-semibold text-teamName text-ink truncate">
            {teamName}
          </p>
          {tier && (
            <p className="text-[11px] text-inkSub truncate">
              {tier}
            </p>
          )}
        </div>
      </div>

      {/* Spacer — pushes the role pill to the right edge via ml-auto.
          (Can't use justify-between now that we have three regions
          and the middle one is absolute-positioned.) */}
      <div className="ml-auto" />

      {/* Right cluster: role pill */}
      {role && (
        <span
          className={[
            'flex-shrink-0',
            'font-sans font-extrabold uppercase',
            'text-[10px] leading-none tracking-[0.6px]',
            'rounded-full border px-2 py-1',
            roleTone,
          ].join(' ')}
        >
          {role}
        </span>
      )}
    </header>
  );
}